import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SERVICES } from "../data/services";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";

export default function ServicesPage() {
  useSEO({
    title: "NDIS Services in Melbourne & Perth | SIL, SDA, Coordination | Alpha Community Care",
    description: "Full-service registered NDIS provider — Supported Independent Living (SIL), Specialist Disability Accommodation (SDA), Support Coordination, Community Nursing, Forensic Disability Services and In-Home Support across Melbourne and Perth.",
    keywords: ["NDIS services Melbourne", "NDIS services Perth", "SIL provider", "SDA provider", "support coordination NDIS", "community nursing NDIS", "forensic disability NDIS"],
    canonical: `${SITE_URL}/services`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])],
  });
  return (
    <div data-testid="page-services">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-12">
          <div className="alpha-overline">Our services</div>
          <h1 className="mt-3 text-4xl sm:text-[56px] font-display font-medium tracking-tight leading-[1.05]">A full set of NDIS supports — delivered properly.</h1>
          <p className="mt-5 max-w-2xl text-lg text-[color:var(--alpha-mute)]">From daily living and community nursing to specialist accommodation and forensic-overlay SIL, our services are shaped around what each participant actually needs.</p>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.Icon;
            return (
              <Link key={s.slug} to={`/services/${s.slug}`} className={`alpha-card p-7 flex flex-col gap-3 group alpha-fade-up alpha-delay-${(i % 4) + 1}`} data-testid={`service-card-${s.slug}`}>
                <div className="w-12 h-12 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center group-hover:bg-[color:var(--alpha-purple-700)] group-hover:text-[color:var(--alpha-purple-50)] transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="alpha-overline !text-[color:var(--alpha-mute)]">NDIS</div>
                <h3 className="font-display text-xl font-semibold leading-snug">{s.title}</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] leading-relaxed">{s.intro}</p>
                <ul className="mt-1 space-y-1.5">
                  {s.inclusions.slice(0, 3).map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] text-[color:var(--alpha-ink)]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-600)] shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                <span className="mt-auto text-sm font-semibold text-[color:var(--alpha-purple-700)] inline-flex items-center gap-1 pt-3">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </Link>
            );
          })}
        </div>

        <div className="alpha-container mt-14">
          <div className="relative overflow-hidden rounded-[28px] alpha-gradient p-10 sm:p-14 text-[color:var(--alpha-purple-50)] flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-medium">Not sure what you need?</h3>
              <p className="mt-2 text-white/85 max-w-xl">Our intake team will walk you through your plan, your goals, and recommend the right mix of supports. No jargon, no pressure.</p>
            </div>
            <Link to="/contact" className="bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-800)] rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2" data-testid="services-contact-cta">
              Talk to our team <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
